import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { instance } from '@app/api/axios';
import { useShowToaster } from '@shared/ui/messages/useToaster';
import {
  getCryptoName,
  truncateBalance,
  FRACTION_DIGITS,
} from '@utils/balance-helpers';

export async function exchangeBalance(data) {
  const res = await instance.post('/balance/exchange', data);
  return res?.data?.rates;
}

function parseAmount(value) {
  const amount = Number(value);
  return amount > 0 ? amount : 0;
}

export function useExchange() {
  const showToaster = useShowToaster();
  const showToasterRef = useRef(showToaster);

  useEffect(() => {
    showToasterRef.current = showToaster;
  }, [showToaster]);

  const exchange = useCallback(async (data) => {
    try {
      return await exchangeBalance(data);
    } catch (err) {
      showToasterRef.current('error', err?.response?.data?.message);
    }
  }, []);

  return { exchange };
}

export function useExchangeTransfer({ amount, fromBank, toBank }) {
  const { exchange } = useExchange();
  const [exchangeDisplay, setExchangeDisplay] = useState(null);
  const exchangeRequestIdRef = useRef(0);

  const debouncedExchangeTransfer = useMemo(
    () =>
      debounce(async (amountValue, from, to) => {
        const parsedAmount = parseAmount(amountValue);
        const requestAmount = parsedAmount || 1;
        const requestId = ++exchangeRequestIdRef.current;

        const rates = await exchange({
          type: 'transfer',
          from: getCryptoName(from.currency),
          to: getCryptoName(to.currency),
          amount: requestAmount,
        });

        if (requestId !== exchangeRequestIdRef.current) return;

        if (rates == null || Number.isNaN(Number(rates))) {
          setExchangeDisplay(null);
          return;
        }

        setExchangeDisplay({
          rate: truncateBalance(
            Number(rates) / requestAmount,
            null,
            FRACTION_DIGITS.EXCHANGE_RATES,
          ),
          fromCurrency: from.currency,
          toCurrency: to.currency,
          expectedAmount: parsedAmount ? rates : 0,
        });
      }, 400),
    [exchange],
  );

  const cancelPending = useCallback(() => {
    debouncedExchangeTransfer.cancel();
    exchangeRequestIdRef.current += 1;
  }, [debouncedExchangeTransfer]);

  useEffect(() => cancelPending, [cancelPending]);

  useEffect(() => {
    if (!fromBank || !toBank) {
      cancelPending();
      setExchangeDisplay(null);
      return;
    }

    if (getCryptoName(fromBank.currency) === getCryptoName(toBank.currency)) {
      cancelPending();
      setExchangeDisplay({
        rate: 1,
        fromCurrency: fromBank.currency,
        toCurrency: toBank.currency,
        expectedAmount: parseAmount(amount),
      });
      return;
    }

    // При смене пары валют прячем старый курс, чтобы символы
    // не обновлялись раньше цифр. При смене только amount — оставляем.
    setExchangeDisplay((prev) => {
      if (
        prev &&
        prev.fromCurrency === fromBank.currency &&
        prev.toCurrency === toBank.currency
      ) {
        return prev;
      }
      return null;
    });

    debouncedExchangeTransfer(amount, fromBank, toBank);
  }, [amount, cancelPending, debouncedExchangeTransfer, fromBank, toBank]);

  return { exchangeDisplay };
}
